import React, { useState } from 'react';
import { Box, Typography, Stack } from 'omaha-design-system';
import type { OptionalFilters, HomeAway } from './types';
import { LEVERAGE_OPTIONS, PERIOD_OPTIONS, NBA_TEAMS, HOME_AWAY_OPTIONS } from './types';

interface NbaRatesOptionalFiltersProps {
  filters: OptionalFilters;
  currentTeamId: string;
  onChange: (filters: OptionalFilters) => void;
}

const NbaRatesOptionalFilters: React.FC<NbaRatesOptionalFiltersProps> = ({
  filters,
  currentTeamId,
  onChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasActiveFilters =
    filters.leverage.length > 0 ||
    filters.periods.length > 0 ||
    filters.fromMargin !== null ||
    filters.toMargin !== null ||
    filters.fromDate !== null ||
    filters.toDate !== null ||
    filters.opponent !== null ||
    filters.homeAway !== null;

  const toggleLeverage = (value: string) => {
    const newLeverage = filters.leverage.includes(value)
      ? filters.leverage.filter((l) => l !== value)
      : [...filters.leverage, value];
    onChange({ ...filters, leverage: newLeverage });
  };

  const togglePeriod = (value: number) => {
    const newPeriods = filters.periods.includes(value)
      ? filters.periods.filter((p) => p !== value)
      : [...filters.periods, value];
    onChange({ ...filters, periods: newPeriods });
  };

  const clearAllFilters = () => {
    onChange({
      leverage: [],
      periods: [],
      fromMargin: null,
      toMargin: null,
      fromDate: null,
      toDate: null,
      opponent: null,
      homeAway: null,
    });
  };

  const opponentTeams = NBA_TEAMS.filter((t) => t.id !== currentTeamId);

  return (
    <Box sx={{ mt: 1.5 }}>
      {/* Toggle Header */}
      <Box
        component="button"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          p: 0,
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            color: '#8B95A5',
            fontSize: '0.75rem',
          }}
        >
          {'\u25B6'}
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: '#8B95A5',
            '&:hover': { color: 'white' },
            transition: 'color 0.15s',
          }}
        >
          Optional Filters
        </Typography>
        {hasActiveFilters && (
          <Box
            sx={{
              ml: 1,
              px: 1,
              py: 0.25,
              fontSize: '0.7rem',
              bgcolor: '#3B82F6',
              color: 'white',
              borderRadius: '9999px',
            }}
          >
            Active
          </Box>
        )}
      </Box>

      {/* Filter Panel */}
      {isExpanded && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            bgcolor: '#1A1D24',
            borderRadius: 1,
            border: '1px solid #2A2F3A',
          }}
        >
          <Stack spacing={1.5}>
            {/* Leverage Multi-Select */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#8B95A5' }}>
                  Leverage (Game Situation)
                </Typography>
                {filters.leverage.length > 0 && (
                  <Box
                    component="button"
                    onClick={() => onChange({ ...filters, leverage: [] })}
                    sx={{
                      bgcolor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      '&:hover': { color: '#ef4444' },
                    }}
                  >
                    Clear
                  </Box>
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {LEVERAGE_OPTIONS.map((opt) => (
                  <Box
                    key={opt.value}
                    component="button"
                    onClick={() => toggleLeverage(opt.value)}
                    title={opt.description}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.8rem',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: filters.leverage.includes(opt.value)
                        ? '#3B82F6'
                        : '#2A2F3A',
                      bgcolor: filters.leverage.includes(opt.value)
                        ? '#3B82F6'
                        : '#252830',
                      color: filters.leverage.includes(opt.value)
                        ? 'white'
                        : '#8B95A5',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        color: 'white',
                      },
                    }}
                  >
                    {opt.label}
                  </Box>
                ))}
              </Stack>
              <Typography sx={{ mt: 0.75, fontSize: '0.7rem', color: '#6B7280' }}>
                Filter by game situation leverage. Excludes garbage time by default when Medium+ selected.
              </Typography>
            </Box>

            {/* Period Multi-Select */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#8B95A5' }}>
                  Period
                </Typography>
                {filters.periods.length > 0 && (
                  <Box
                    component="button"
                    onClick={() => onChange({ ...filters, periods: [] })}
                    sx={{
                      bgcolor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      '&:hover': { color: '#ef4444' },
                    }}
                  >
                    Clear
                  </Box>
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {PERIOD_OPTIONS.map((opt) => (
                  <Box
                    key={opt.value}
                    component="button"
                    onClick={() => togglePeriod(opt.value)}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.8rem',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: filters.periods.includes(opt.value)
                        ? '#3B82F6'
                        : '#2A2F3A',
                      bgcolor: filters.periods.includes(opt.value)
                        ? '#3B82F6'
                        : '#252830',
                      color: filters.periods.includes(opt.value)
                        ? 'white'
                        : '#8B95A5',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        color: 'white',
                      },
                    }}
                  >
                    {opt.label}
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Home/Away and Opponent Row */}
            <Stack direction="row" spacing={2}>
              {/* Home/Away */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#8B95A5', mb: 1 }}>
                  Home/Away
                </Typography>
                <Box
                  component="select"
                  value={filters.homeAway ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    onChange({
                      ...filters,
                      homeAway: e.target.value === '' ? null : (e.target.value as HomeAway),
                    })
                  }
                  sx={{
                    width: '100%',
                    px: 1.5,
                    py: 1,
                    fontSize: '0.8rem',
                    border: '1px solid #2A2F3A',
                    borderRadius: 1,
                    bgcolor: '#252830',
                    color: '#E8EAED',
                    outline: 'none',
                    '&:focus': {
                      borderColor: '#3B82F6',
                    },
                  }}
                >
                  {HOME_AWAY_OPTIONS.map((opt) => (
                    <option key={opt.value ?? 'all'} value={opt.value ?? ''}>
                      {opt.label}
                    </option>
                  ))}
                </Box>
              </Box>

              {/* Opponent */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#8B95A5', mb: 1 }}>
                  Opponent
                </Typography>
                <Box
                  component="select"
                  value={filters.opponent ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    onChange({
                      ...filters,
                      opponent: e.target.value === '' ? null : e.target.value,
                    })
                  }
                  sx={{
                    width: '100%',
                    px: 1.5,
                    py: 1,
                    fontSize: '0.8rem',
                    border: '1px solid #2A2F3A',
                    borderRadius: 1,
                    bgcolor: '#252830',
                    color: '#E8EAED',
                    outline: 'none',
                    '&:focus': {
                      borderColor: '#3B82F6',
                    },
                  }}
                >
                  <option value="">All Opponents</option>
                  {opponentTeams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Box>
              </Box>
            </Stack>

            {/* Score Margin Range */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#8B95A5', mb: 1 }}>
                Score Margin Range
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  component="input"
                  type="number"
                  placeholder="Min"
                  value={filters.fromMargin ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange({
                      ...filters,
                      fromMargin: e.target.value === '' ? null : parseInt(e.target.value),
                    })
                  }
                  sx={{
                    width: '80px',
                    px: 1.5,
                    py: 1,
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    border: '1px solid #2A2F3A',
                    borderRadius: 1,
                    bgcolor: '#252830',
                    color: '#E8EAED',
                    outline: 'none',
                    '&:focus': { borderColor: '#3B82F6' },
                  }}
                />
                <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>to</Typography>
                <Box
                  component="input"
                  type="number"
                  placeholder="Max"
                  value={filters.toMargin ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange({
                      ...filters,
                      toMargin: e.target.value === '' ? null : parseInt(e.target.value),
                    })
                  }
                  sx={{
                    width: '80px',
                    px: 1.5,
                    py: 1,
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    border: '1px solid #2A2F3A',
                    borderRadius: 1,
                    bgcolor: '#252830',
                    color: '#E8EAED',
                    outline: 'none',
                    '&:focus': { borderColor: '#3B82F6' },
                  }}
                />
                <Typography sx={{ fontSize: '0.7rem', color: '#6B7280' }}>
                  (e.g., -5 to 5 for close games)
                </Typography>
              </Stack>
            </Box>

            {/* Date Range */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#8B95A5', mb: 1 }}>
                Date Range
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  component="input"
                  type="date"
                  value={filters.fromDate ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange({
                      ...filters,
                      fromDate: e.target.value === '' ? null : e.target.value,
                    })
                  }
                  sx={{
                    px: 1.5,
                    py: 1,
                    fontSize: '0.8rem',
                    border: '1px solid #2A2F3A',
                    borderRadius: 1,
                    bgcolor: '#252830',
                    color: '#E8EAED',
                    outline: 'none',
                    '&:focus': { borderColor: '#3B82F6' },
                  }}
                />
                <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>to</Typography>
                <Box
                  component="input"
                  type="date"
                  value={filters.toDate ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange({
                      ...filters,
                      toDate: e.target.value === '' ? null : e.target.value,
                    })
                  }
                  sx={{
                    px: 1.5,
                    py: 1,
                    fontSize: '0.8rem',
                    border: '1px solid #2A2F3A',
                    borderRadius: 1,
                    bgcolor: '#252830',
                    color: '#E8EAED',
                    outline: 'none',
                    '&:focus': { borderColor: '#3B82F6' },
                  }}
                />
              </Stack>
            </Box>

            {/* Clear All */}
            {hasActiveFilters && (
              <Box sx={{ pt: 1, borderTop: '1px solid #2A2F3A' }}>
                <Box
                  component="button"
                  onClick={clearAllFilters}
                  sx={{
                    bgcolor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    color: '#6B7280',
                    '&:hover': { color: '#ef4444' },
                    transition: 'color 0.15s',
                  }}
                >
                  Clear All Optional Filters
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default NbaRatesOptionalFilters;
